import { type FC, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { IconMinus, IconPlus } from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { RadioButton, SwitchButton } from 'components-ref/atoms'
import { useModalContext } from 'context/modalContext'
import { type ListSorting } from 'types/types'

import './_sortModal.scss'

type ToggleMap = Record<string, boolean>

type SectionKey = 'first_section' | 'second_section' | 'third_section' | 'fourth_section'

const sectionKeys: SectionKey[] = [
  'first_section',
  'second_section',
  'third_section',
  'fourth_section',
]

const SortModal: FC<{
  elements?: ListSorting
  applyFn?: (...args: any[]) => void
  filter?: string
  sort?: string
  sortingProps?: {
    selectedSort?: string | boolean
    selectedOrder?: string
    selectProps?: Record<string, string | number>
    selectedShowDuplicates?: boolean
    selectedRemovedMedia?: boolean
    paidOnly?: boolean
    unreleasedOnly?: boolean
    allTimeStats?: boolean
    filterOption?: string
    initialToggles?: ToggleMap
  }
  hasResetBtn?: boolean
  resetFn?: (...args: any[]) => void
  dontClearModal?: boolean
  selectedSort?: { selectedSort?: string } | { value?: string } | string
  selectedOrder?: { selectedOrder?: string } | { value?: string } | string
  activeFilter?: any
  defaultOrderBySort?: Record<string, string>
}> = ({
  elements,
  applyFn,
  sortingProps,
  hasResetBtn,
  resetFn,
  dontClearModal,
  selectedSort,
  selectedOrder,
  defaultOrderBySort,
}) => {
  const default1 = (typeof selectedSort === 'object'
    ? (selectedSort as any)?.selectedSort ?? (selectedSort as any)?.value
    : selectedSort) ?? sortingProps?.selectedSort ?? ''

  const default2 = (typeof selectedOrder === 'object'
    ? (selectedOrder as any)?.selectedOrder ?? (selectedOrder as any)?.value
    : selectedOrder) ?? sortingProps?.selectedOrder ?? ''

  const default3 = sortingProps?.filterOption ?? ''

  const [tempVal, setTempVal] = useState<string | number>(default1)
  const [tempVal1, setTempVal1] = useState<string | number>(default2)
  const [tempVal2, setTempVal2] = useState<string | number>(default3)
  const [selectTempVal, setSelectTempVal] = useState<Record<string, unknown>>(
    sortingProps?.selectProps ?? {}
  )

  const [toggleStates, setToggleStates] = useState<ToggleMap>({})

  const modalData = useModalContext()
  const { t } = useTranslation()

  const initialToggles: ToggleMap = useMemo(() => {
    const init: ToggleMap = { ...(sortingProps?.initialToggles ?? {}) }

    sectionKeys.forEach(sectionKey => {
      ;(elements as any)?.[sectionKey]?.forEach((item: any) => {
        if (!item?.isToggle || !item?.value) return

        const key = item.value

        if (key in init) return

        if (key === 'show_removed_media' && typeof sortingProps?.selectedRemovedMedia === 'boolean') {
          init[key] = !!sortingProps.selectedRemovedMedia
          return
        }

        if (key === 'show_duplicates' && typeof sortingProps?.selectedShowDuplicates === 'boolean') {
          init[key] = !!sortingProps.selectedShowDuplicates
          return
        }

        if (key === 'paid_only' && typeof sortingProps?.paidOnly === 'boolean') {
          init[key] = !!sortingProps.paidOnly
          return
        }

        if (key === 'unreleased_only' && typeof sortingProps?.unreleasedOnly === 'boolean') {
          init[key] = !!sortingProps.unreleasedOnly
          return
        }
        if (key === 'all_time_stats' && typeof sortingProps?.allTimeStats === 'boolean') {
          init[key] = !!sortingProps.allTimeStats
          return
        }

        init[key] = false
      })
    })

    return init
  }, [elements, sortingProps])

  useEffect(() => {
    setTempVal(default1)
    setTempVal1(default2)
    setTempVal2(default3)
    setSelectTempVal(sortingProps?.selectProps ?? {})
    setToggleStates(initialToggles)
  }, [default1, default2, default3, sortingProps?.selectProps, initialToggles])

  const renderSelectedValue = (key: string | number) => {
    let selectedValue: any = null
    const names = Object.keys(selectTempVal)

    for (let i = 0; i < names.length; i++) {
      if (names[i] === key) {
        selectedValue = (selectTempVal as any)[names[i]]
      }
    }

    return selectedValue
  }

  const checkIfSelected = (key: string | number, value: string | number) => {
    const names = Object.keys(selectTempVal)

    for (let i = 0; i < names.length; i++) {
      if (names[i] === key) {
        return (selectTempVal as any)[names[i]] === value
      }
    }

    return false
  }

  const setNextSelected = (
    array: Array<{ label: string | number; value: string | number }>,
    key: string | number,
    prevOrNext: 'prev' | 'next'
  ) => {
    let newIndex = 0

    for (let i = 0; i < array.length; i++) {
      if (array[i].value === (selectTempVal as any)[key]) {
        newIndex = prevOrNext === 'next'
          ? array.indexOf(array[i]) + 1
          : array.indexOf(array[i]) - 1
      }
    }

    if (newIndex >= 0 && newIndex <= array.length - 1) {
      setSelectTempVal(prev => ({ ...(prev as any), [key]: array[newIndex].value }))
    }
  }

const getToggle = (val: string) => !!toggleStates[val]

const massMessageConflictingToggles = [
  'group_by_post',
  'show_duplicates',
  'unreleased_only',
]

const isToggleDisabled = (val: string) => {
  const massMessagesEnabled = getToggle('mass_messages')

  // Only All Time Stats requires Group by Post/Message to be OFF.
  if (
    val === 'all_time_stats' &&
    getToggle('group_by_post')
  ) {
    return true
  }

  // Mass Messages cannot be combined with these filters.
  if (
    massMessagesEnabled &&
    massMessageConflictingToggles.includes(val)
  ) {
    return true
  }

  // Mass Messages itself is disabled while a conflicting option is enabled.
  if (
    val === 'mass_messages' &&
    massMessageConflictingToggles.some(toggle => getToggle(toggle))
  ) {
    return true
  }

  if (
    val === 'all_time_stats' &&
    getToggle('unreleased_only')
  ) {
    return true
  }

  return false
}

const flipToggle = (val: string) => {
  setToggleStates(prev => {
    const nextValue = !prev[val]

    const next: ToggleMap = {
      ...prev,
      [val]: nextValue,
    }

    // All Time Stats requires Group Duplicates.
    if (val === 'all_time_stats' && nextValue) {
      next.show_duplicates = true
    }

    // Turning Group Duplicates off also turns All Time Stats off.
    if (val === 'show_duplicates' && !nextValue) {
      next.all_time_stats = false
    }

  if (val === 'group_by_post' && nextValue) {
    next.show_duplicates = false
    next.all_time_stats = false
  }

    // Unreleased Only cannot be combined with All Time Stats.
    if (val === 'unreleased_only' && nextValue) {
      next.all_time_stats = false
    }

    return next
  })
}
  const getSectionClassName = (sectionKey: SectionKey, sectionIndex: number) => {
    const classes = ['sortModal__sort']

    if (sectionIndex > 0) {
      classes.push('sortModal__sort__section')
      classes.push(`sortModal__sort__${sectionIndex + 1}`)
    }

    if (sectionKey === 'second_section') classes.push('sortModal__sort__2nd')
    if (sectionKey === 'third_section') classes.push('sortModal__sort__3rd')
    if (sectionKey === 'fourth_section') classes.push('sortModal__sort__4th')

    return classes.join(' ')
  }

  const getRadioSelected = (sectionKey: SectionKey, itemValue: string) => {
    if (sectionKey === 'first_section') return tempVal === itemValue
    if (sectionKey === 'fourth_section') return tempVal2 === itemValue

    return tempVal1 === itemValue
  }

  const handleRadioClick = (
    sectionKey: SectionKey,
    itemValue: string
  ) => {
    if (sectionKey === 'first_section') {
      setTempVal(prev => {
        const isDeselecting = prev === itemValue

        if (isDeselecting) {
          return ''
        }

        const defaultOrder = defaultOrderBySort?.[itemValue]

        if (defaultOrder) {
          setTempVal1(defaultOrder)
        }

        return itemValue
      })

      return
    }

    if (sectionKey === 'fourth_section') {
      setTempVal2(prev => (prev === itemValue ? '' : itemValue))
      return
    }

    setTempVal1(itemValue)
  }

  const renderSection = (sectionKey: SectionKey, sectionIndex: number) => {
    const section = (elements as any)?.[sectionKey]

    if (!section?.length) return null

    return (
      <ul
        className={getSectionClassName(sectionKey, sectionIndex)}
        key={sectionKey}
      >
        {section.map(
          (
            item: {
              value: string
              name: string
              subText?: string | ((ctx: { getToggle: (k: string) => boolean }) => string)
              selectOptions?: Array<{ value: string | number; label: string | number }>
              isToggle?: boolean
            },
            key: number
          ) => {
            const isToggle = !!item.isToggle
            const disabled = isToggle && isToggleDisabled(item.value)

            return (
              <li
                className='sortModal__sort__item'
                key={key}
                style={disabled ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
                onClick={() => {
                  if (disabled) return

                  if (isToggle) {
                    flipToggle(item.value)
                    return
                  }

                  handleRadioClick(sectionKey, item.value)
                }}
              >
                <div className='sortModal__sort__item__label'>
                  <span className='sortModal__sort__item__name'>{item.name}</span>

                  {(() => {
                    const computed =
                      typeof item.subText === 'function'
                        ? item.subText({ getToggle })
                        : item.subText

                    return computed ? (
                      <div className='sortModal__sort__item__subtext'>{computed}</div>
                    ) : null
                  })()}
                </div>

                {item.selectOptions && (
                  <div
                    className='sort-modal-select-options-container'
                    onClick={(e: any) => e.stopPropagation()}
                  >
                    <div
                      className='sort-modal-select-options-nav-buttons'
                      onClick={() => setNextSelected(item.selectOptions ?? [], item.value, 'prev')}
                    >
                      <IconMinus />
                    </div>

                    <label
                      htmlFor={`sortModalSelectCheck${item.value}`}
                      className={`sort-modal-select-options-selected ${
                        tempVal === item.value ? 'sort-modal-select-options-selected-active' : ''
                      }`}
                    >
                      {sortingProps?.selectProps &&
                        item.selectOptions.filter(
                          (option: any) => option.value === renderSelectedValue(item.value)
                        )[0]?.label}
                    </label>

                    <div
                      className='sort-modal-select-options-nav-buttons'
                      onClick={() => setNextSelected(item.selectOptions ?? [], item.value, 'next')}
                    >
                      <IconPlus color='#9B9B9B' width='10' height='10' />
                    </div>

                    <input
                      type='checkbox'
                      id={`sortModalSelectCheck${item.value}`}
                      className='sort-modal-select-options-checkbox'
                    />

                    <label
                      htmlFor={`sortModalSelectCheck${item.value}`}
                      className='sort-modal-select-options-all-options-overlay'
                    />

                    <div className='sort-modal-select-options-all-options'>
                      {item.selectOptions.map((option: any) => (
                        <label
                          key={option.value}
                          htmlFor={`sortModalSelectCheck${item.value}`}
                          className={`sort-modal-select-options-single-option ${
                            checkIfSelected(item.value, option.value)
                              ? 'sort-modal-select-options-single-option-selected'
                              : ''
                          }`}
                          onClick={() =>
                            setSelectTempVal(prev => ({
                              ...(prev as any),
                              [item.value]: option.value,
                            }))
                          }
                        >
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {!isToggle ? (
                  <RadioButton active={getRadioSelected(sectionKey, item.value)} />
                ) : (
                  <SwitchButton
                    active={getToggle(item.value)}
                    toggle={() => flipToggle(item.value)}
                  />
                )}
              </li>
            )
          }
        )}
      </ul>
    )
  }

  return (
    <div className='sortModal__sort__wrapper'>
      {sectionKeys.map((sectionKey, sectionIndex) =>
        renderSection(sectionKey, sectionIndex)
      )}

      <div className='sortModal__buttons'>
        {hasResetBtn && (
          <Button
            text={t('reset')}
            color='grey'
            font='mont-14-normal'
            width='10'
            height='3'
            clickFn={() => resetFn?.(setTempVal, setTempVal1, setSelectTempVal, setTempVal2)}
            customClass='sortModal--btn'
          />
        )}

        <Button
          text={t('apply')}
          color='black'
          font='mont-14-normal'
          width='13'
          height='3'
          clickFn={() => {
            applyFn?.(
              tempVal,
              tempVal1,
              selectTempVal,
              toggleStates
            )

            if (!dontClearModal) modalData.clearModal()
          }}
          customClass='sortModal--btn'
        />
      </div>
    </div>
  )
}

export default SortModal